import { type BaseEntity } from './base-entity';
import { JSON_API_PROPERTY_DECORATOR_KEY } from './decorators/json-api-property.decorator';
import { JSON_API_RELATIONSHIP_DECORATOR_KEY } from './decorators/json-api-relationship.decorator';
import { JSON_API_RESOURCE_DECORATOR_KEY } from './decorators/json-api-resource.decorator';
import { JsonApiSerializer } from './jsonapi-serializer';

import type { ErrorType, JsonApiDocument } from './types';

export class EntityManager {
  private readonly resources: Record<string, typeof BaseEntity> = {};
  private serializer: JsonApiSerializer | null = null;

  public registerResource = (type: string, resource: typeof BaseEntity): void => {
    this.resources[type] = resource;
  };

  public generateSchema = (): Record<string, any> => {
    const schema = Object.entries(this.resources)
      .map(([type, resource]: [string, any]) => {
        const propertiesMetadata = Reflect.getMetadata(JSON_API_PROPERTY_DECORATOR_KEY, resource);
        const relationshipsMetadata = Reflect.getMetadata(
          JSON_API_RELATIONSHIP_DECORATOR_KEY,
          resource
        );
        const properties = Object.entries(new resource.prototype.constructor()).filter(
          ([prop, _v]) => prop !== 'id'
        );
        const fields = properties
          .filter(([prop, _value]) => !relationshipsMetadata?.[prop])
          .map(
            ([prop, value]) =>
              [
                prop,
                Object.assign(
                  { readOnly: propertiesMetadata?.[prop]?.readOnly },
                  propertiesMetadata?.[prop]?.resolver
                    ? { resolve: propertiesMetadata?.[prop].resolver }
                    : { type: propertiesMetadata?.[prop]?.type ?? this.getType(value) }
                ),
              ] as [string, any]
          )
          .filter(([_prop, value]) => value !== null)
          // eslint-disable-next-line no-sequences
          .reduce((acc: Record<string, any>, cur) => ((acc[cur[0]] = cur[1]), acc), {});
        const relationships = properties
          .filter(([prop, _value]) => relationshipsMetadata?.[prop])
          .map(([prop, _value]) => [prop, { type: relationshipsMetadata?.[prop] }] as [string, any])
          // eslint-disable-next-line no-sequences
          .reduce((acc: Record<string, any>, cur) => ((acc[cur[0]] = cur[1]), acc), {});
        return [
          type,
          {
            type,
            fields,
            relationships,
          },
        ] as [string, any];
      })
      // eslint-disable-next-line no-sequences
      .reduce((acc: Record<string, any>, cur) => ((acc[cur[0]] = cur[1]), acc), {});

    this.initializeSerializer(schema);

    return schema;
  };

  public serialize<T extends BaseEntity>(entity: T): Record<any, any> | null {
    return (
      this.serializer?.serialize(
        Reflect.getMetadata(JSON_API_RESOURCE_DECORATOR_KEY, entity.constructor).type,
        entity
      ) ?? null
    );
  }

  public deserialize = (
    document: JsonApiDocument
  ): { data?: BaseEntity; error?: ErrorType } | undefined => {
    const obj = this.serializer?.deserialize(document);
    if (!obj) return undefined;
    if (obj.error) {
      return obj;
    }
    return obj; // this.resources[obj.data.type].prototype.constructor(obj);
  };

  private readonly initializeSerializer = (schema: Record<string, any>): void => {
    this.serializer = new JsonApiSerializer({ schema });
  };

  private readonly getType = (val: any): string | null => {
    if (val instanceof String || typeof val === 'string') return 'string';
    if (val instanceof Number || typeof val === 'number') return 'number';
    if (val instanceof Date) return 'date';
    if (val instanceof Boolean || typeof val === 'boolean') return 'boolean';
    return null;
  };
}

export default new EntityManager();
